import type { TFunction } from 'i18next';

import type { CadeCodedField } from '@/services/types';

/** Fields whose values come from a vocabulary, and which one. Mirrors `ENUM_OF` in
 *  `analytics/cade/codings.py`; a value outside it still renders, as its own code. */
const ENUM_OF: Record<string, string> = {
  conduct_type: 'conduct_type',
  theory_of_harm: 'theory_of_harm',
  geographic_scope: 'geographic_scope',
  remedy_type: 'remedy_type',
  sg_position: 'position',
  procade_position: 'position',
  mpf_position: 'position',
};

const AMOUNTS = new Set(['fine_total_brl', 'tcc_contribution_brl']);

/**
 * One coded value as text in the reader's language: enum codes through their labels, lists
 * joined, amounts as currency, booleans as yes/no. Shared with the compare screen so a value
 * reads the same in both places.
 */
export function codedText(field: CadeCodedField, t: TFunction, language: string): string {
  const { value } = field;
  const vocabulary = ENUM_OF[field.field];
  const label = (code: string) =>
    vocabulary ? t(`cade:codings.values.${vocabulary}.${code}`, { defaultValue: code }) : code;
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return t(value ? 'cade:codings.yes' : 'cade:codings.no');
  if (typeof value === 'number') {
    return AMOUNTS.has(field.field)
      ? new Intl.NumberFormat(language, {
          style: 'currency',
          currency: 'BRL',
        }).format(value)
      : new Intl.NumberFormat(language).format(value);
  }
  if (Array.isArray(value)) return value.map(label).join('; ');
  return label(value);
}
